/**
 * DXF DRAWING CALIBRATION
 * ───────────────────────
 * A dedicated preprocessing step that determines the *physical* meaning of a
 * drawing's coordinate space — i.e. how many millimetres one drawing unit
 * represents — *before* the geometry is normalised into canvas space.
 *
 * Why this is separate from rendering:
 *   - The geometry normaliser (`buildFloorPlan` in `dxf.ts`) maps the longest
 *     drawing dimension to a fixed canvas size. It only knows "drawing units →
 *     canvas units". It does NOT know "drawing units → metres".
 *   - AMHS components have real physical dimensions (m/mm) and must be drawn at
 *     the correct size, so we need the missing "drawing units → real world"
 *     relationship.
 *
 * Calibration sources, in descending order of confidence:
 *   1. header     — the DXF `$INSUNITS` value (explicit, most reliable). -- DONE 
 *   2. dimension  — DIMENSION entities: geometric length vs displayed text. -- DONE 
 *   3. scale_bar  — a graphical scale bar (ticks labelled e.g. 0', 20', 40'). -- ?
 *   4. annotation — labelled equipment whose footprint matches its text. -- ?
 *
 * IMPORTANT: if no source resolves, the result is reported as `unknown` rather
 * than silently assuming millimetres. Wrongly assuming mm produces physically
 * incorrect AMHS layouts.
 *
 * The module is intentionally decoupled from the `dxf` parsing library: callers
 * extract abstract samples (`DimensionSample`, `ScaleBarCandidate`, …) from
 * whatever parser they use and hand them to `calibrateDrawing`. This keeps the
 * calibration logic testable and makes new sources easy to add.
 */

export type DxfUnit = 'mm' | 'cm' | 'm' | 'in' | 'ft';

/** Where a calibration result was derived from. */
export type CalibrationSource =
  | 'header'
  | 'dimension'
  | 'scale_bar'
  | 'annotation'
  | 'unknown';

/** Millimetres represented by one unit of each supported physical unit. */
export const MM_PER_UNIT: Record<DxfUnit, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
  ft: 304.8,
};

/**
 * The physical interpretation of a drawing's coordinate space. All ratios are
 * `null` when the units could not be confidently determined (`resolved=false`).
 */
export interface DrawingCalibration {
  /** Detected physical unit, or `null` if only a numeric ratio is known. */
  unit: DxfUnit | null;
  /** Drawing units that equal exactly one metre. */
  drawingUnitsPerMeter: number | null;
  /** Metres represented by one drawing unit. */
  metersPerDrawingUnit: number | null;
  /** Millimetres represented by one drawing unit. */
  millimetersPerDrawingUnit: number | null;
  /** Which source produced the calibration. */
  source: CalibrationSource;
  /** Heuristic confidence in [0, 1]; 1 = explicit header units. */
  confidence: number;
  /** True when a usable physical scale was found. */
  resolved: boolean;
  /** Human-readable provenance for debugging. */
  details?: string;
}

/** Canvas-space physical relationships, derived once normalisation is known. */
export interface CanvasCalibration {
  /** Millimetres represented by one normalised canvas unit. */
  millimetersPerCanvasUnit: number | null;
  /** Canvas units per metre. */
  canvasUnitsPerMeter: number | null;
}

/** A single DIMENSION entity reduced to what calibration needs. */
export interface DimensionSample {
  /** Geometric length of the measured span, in drawing units. */
  measuredDrawingUnits: number;
  /**
   * Candidate displayed strings (the dimension's MTEXT/TEXT graphics, plus any
   * text override). The first one that parses to a real-world length wins.
   */
  texts: string[];
}

/** One labelled tick of a graphical scale bar. */
export interface ScaleBarTick {
  /** Real-world length at this tick, in metres (e.g. 0, 1.524, 3.048 for 0/5/10 ft). */
  meters: number;
  /** Tick position in drawing units. */
  x: number;
  y: number;
}

export interface ScaleBarCandidate {
  ticks: ScaleBarTick[];
}

/** A piece of annotated equipment: stated real size vs measured footprint. */
export interface AnnotationSample {
  /** Stated dimensions in metres (e.g. [2.032, 0.762] for 80" × 30"). */
  meters: number[];
  /** Corresponding measured footprint in drawing units, same order. */
  drawingUnits: number[];
}

export interface CalibrationInput {
  /** `$INSUNITS` value (0 / absent ⇒ unitless ⇒ try other sources). */
  insUnits?: number | string | null;
  dimensions?: DimensionSample[];
  scaleBars?: ScaleBarCandidate[];
  annotations?: AnnotationSample[];
}

export const UNKNOWN_CALIBRATION: DrawingCalibration = {
  unit: null,
  drawingUnitsPerMeter: null,
  metersPerDrawingUnit: null,
  millimetersPerDrawingUnit: null,
  source: 'unknown',
  confidence: 0,
  resolved: false,
  details: 'no calibration source available',
};

// ─── Unit helpers ─────────────────────────────────────────────────────────────

/**
 * Maps a DXF `$INSUNITS` code (or unit string) to a supported unit. Returns
 * `null` for unitless (0) or unsupported codes so the caller falls through to
 * the next calibration source instead of assuming a unit.
 */
export function insUnitsToUnit(value: number | string | null | undefined): DxfUnit | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') {
    switch (value) {
      case 1:
        return 'in';
      case 2:
        return 'ft';
      case 4:
        return 'mm';
      case 5:
        return 'cm';
      case 6:
        return 'm';
      default:
        return null; // 0 = unitless, or an unsupported code
    }
  }
  const u = String(value).trim().toLowerCase();
  if (!u) return null;
  if (u === 'm' || u === 'meter' || u === 'meters' || u === 'metre' || u === 'metres') return 'm';
  if (u === 'cm' || u === 'centimeter' || u === 'centimeters' || u === 'centimetre' || u === 'centimetres') return 'cm';
  if (
    u === 'mm' ||
    u === 'millimeter' ||
    u === 'millimeters' ||
    u === 'millimetre' ||
    u === 'millimetres'
  ) {
    return 'mm';
  }
  if (u === 'in' || u === 'inch' || u === 'inches') return 'in';
  if (u === 'ft' || u === 'foot' || u === 'feet') return 'ft';
  return null;
}

/**
 * Finds the standard unit whose mm/unit ratio matches `mmPerDrawingUnit` within
 * `relTol` (relative). Returns `null` when the ratio is non-standard.
 */
export function matchUnitFromMmPerUnit(
  mmPerDrawingUnit: number,
  relTol = 0.01
): DxfUnit | null {
  let best: DxfUnit | null = null;
  let bestErr = Infinity;
  (Object.keys(MM_PER_UNIT) as DxfUnit[]).forEach((unit) => {
    const ref = MM_PER_UNIT[unit];
    const err = Math.abs(mmPerDrawingUnit - ref) / ref;
    if (err <= relTol && err < bestErr) {
      bestErr = err;
      best = unit;
    }
  });
  return best;
}

/** Builds a resolved calibration from a millimetres-per-drawing-unit ratio. */
function buildCalibration(
  mmPerDrawingUnit: number,
  source: CalibrationSource,
  confidence: number,
  details: string
): DrawingCalibration {
  const millimetersPerDrawingUnit = mmPerDrawingUnit;
  const metersPerDrawingUnit = mmPerDrawingUnit / 1000;
  const drawingUnitsPerMeter = 1 / metersPerDrawingUnit;
  return {
    unit: matchUnitFromMmPerUnit(mmPerDrawingUnit),
    drawingUnitsPerMeter,
    metersPerDrawingUnit,
    millimetersPerDrawingUnit,
    source,
    confidence,
    resolved: true,
    details,
  };
}

/** Builds a resolved calibration directly from a known unit. */
function calibrationFromUnit(
  unit: DxfUnit,
  source: CalibrationSource,
  confidence: number,
  details: string
): DrawingCalibration {
  const cal = buildCalibration(MM_PER_UNIT[unit], source, confidence, details);
  cal.unit = unit; // keep the authoritative unit even if rounding nudged the match
  return cal;
}

// ─── Measurement-text parsing ───────────────────────────────────────────────

/** Strips MTEXT formatting codes / dimension placeholders to bare text. */
export function cleanDimensionText(raw: string): string {
  return raw
    .replace(/\\P/gi, ' ') // paragraph break
    .replace(/\\X/gi, ' ') // text above/below dimension line split
    .replace(/\\[A-Za-z][^;]*;/g, '') // formatting codes (\fArial...; \H0.7x; …)
    .replace(/[{}]/g, '')
    .replace(/<>/g, '') // "<>" = use measured value placeholder
    .replace(/%%[a-zA-Z]/g, '') // %%c (diameter), %%d (degree), …
    .trim();
}

const numFrom = (s: string): number => parseFloat(s);

/**
 * Parses a single real-world length string into metres. Requires an explicit
 * unit — bare numbers are ambiguous and intentionally rejected (returning a
 * value for them would let drawing-unit values masquerade as real lengths).
 */
export function parseLengthToMeters(input: string): number | null {
  const s = (input || '').trim();
  if (!s) return null;

  // Feet (+ optional inches): 5'  5'-6"  5' 6"  5'6"  (', ′ accepted)
  if (/['′]/.test(s)) {
    const fi = s.match(/(-?\d+(?:\.\d+)?)\s*['′]\s*-?\s*(\d+(?:\.\d+)?)?\s*["″]?/);
    if (fi) {
      const feet = numFrom(fi[1]);
      const inches = fi[2] !== undefined ? numFrom(fi[2]) : 0;
      if (Number.isFinite(feet)) return (feet * 12 + inches) * 0.0254;
    }
  }

  // Inches only: 6"  6 in  6 inch  6 inches
  const inMatch = s.match(/(-?\d+(?:\.\d+)?)\s*(?:["″]|in\b|inch(?:es)?\b)/i);
  if (inMatch) return numFrom(inMatch[1]) * 0.0254;

  // Feet by name: 5ft  5 feet  5 foot
  const ftMatch = s.match(/(-?\d+(?:\.\d+)?)\s*(?:ft|feet|foot)\b/i);
  if (ftMatch) return numFrom(ftMatch[1]) * 0.3048;

  // Millimetres (checked before metres so "mm" isn't read as "m").
  const mm = s.match(/(-?\d+(?:\.\d+)?)\s*(?:mm|millimet(?:er|re)s?)\b/i);
  if (mm) return numFrom(mm[1]) * 0.001;

  // Centimetres.
  const cm = s.match(/(-?\d+(?:\.\d+)?)\s*(?:cm|centimet(?:er|re)s?)\b/i);
  if (cm) return numFrom(cm[1]) * 0.01;

  // Metres.
  const m = s.match(/(-?\d+(?:\.\d+)?)\s*(?:m|met(?:er|re)s?)\b/i);
  if (m) return numFrom(m[1]);

  return null;
}

/**
 * Parses a dimension's displayed text into a real-world length (metres).
 * Alternate-units in square brackets (AutoCAD `primary [alternate]`) are
 * preferred because that is where the real-world unit is most often stated.
 */
export function parseDimensionText(raw: string): number | null {
  const cleaned = cleanDimensionText(raw || '');
  if (!cleaned) return null;

  const brackets = cleaned.match(/\[([^\]]+)\]/g);
  if (brackets) {
    for (const b of brackets) {
      const v = parseLengthToMeters(b.slice(1, -1));
      if (v && v > 0) return v;
    }
  }
  const whole = parseLengthToMeters(cleaned);
  return whole && whole > 0 ? whole : null;
}

// ─── Statistical helpers ─────────────────────────────────────────────────────

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}

/**
 * Reduces noisy mm/unit samples to a single agreed ratio. Returns `null` when
 * the samples conflict (i.e. fewer than `minAgreement` of them cluster around
 * the median within `relTol`), signalling the source is unreliable.
 */
function consensus(
  samples: number[],
  relTol: number,
  minAgreement = 0.6
): { value: number; inliers: number; total: number; median: number } | null {
  const valid = samples.filter((v) => Number.isFinite(v) && v > 0);
  if (valid.length === 0) return null;
  const med = median(valid);
  const inliers = valid.filter((v) => Math.abs(v - med) <= relTol * med);
  if (inliers.length < Math.max(1, Math.ceil(valid.length * minAgreement))) return null;
  const value = inliers.reduce((a, b) => a + b, 0) / inliers.length;
  return { value, inliers: inliers.length, total: valid.length, median: med };
}

// ─── Individual detectors ────────────────────────────────────────────────────

function calibrateFromHeader(insUnits: CalibrationInput['insUnits']): DrawingCalibration | null {
  const unit = insUnitsToUnit(insUnits);
  if (!unit) return null;
  return calibrationFromUnit(unit, 'header', 1, `$INSUNITS=${insUnits} → ${unit}`);
}

function calibrateFromDimensions(samples: DimensionSample[]): DrawingCalibration | null {
  const mmPerUnit: number[] = [];
  for (const s of samples) {
    if (!(s.measuredDrawingUnits > 0)) continue;
    for (const t of s.texts) {
      const meters = parseDimensionText(t);
      if (meters && meters > 0) {
        mmPerUnit.push((meters * 1000) / s.measuredDrawingUnits);
        break; // one usable text per dimension
      }
    }
  }
  const agreed = consensus(mmPerUnit, 0.02);
  if (!agreed) return null;
  // More agreeing dimensions ⇒ more confidence (cap below header's 1.0).
  const confidence = Math.min(0.95, 0.55 + 0.1 * agreed.inliers);
  return buildCalibration(
    agreed.value,
    'dimension',
    confidence,
    `dimensions: ${agreed.inliers}/${agreed.total} agree, median ${agreed.median.toFixed(4)} mm/unit`
  );
}

function calibrateFromScaleBars(bars: ScaleBarCandidate[]): DrawingCalibration | null {
  for (const bar of bars) {
    const ticks = (bar.ticks || []).filter((t) => Number.isFinite(t.meters));
    if (ticks.length < 2) continue;
    let lo = ticks[0];
    let hi = ticks[0];
    for (const t of ticks) {
      if (t.meters < lo.meters) lo = t;
      if (t.meters > hi.meters) hi = t;
    }
    const dMeters = hi.meters - lo.meters;
    const dUnits = Math.hypot(hi.x - lo.x, hi.y - lo.y);
    if (dMeters > 0 && dUnits > 0) {
      return buildCalibration(
        (dMeters * 1000) / dUnits,
        'scale_bar',
        0.7,
        `scale bar: ${ticks.length} ticks span ${dMeters.toFixed(3)} m / ${dUnits.toFixed(3)} units`
      );
    }
  }
  return null;
}

function calibrateFromAnnotations(anns: AnnotationSample[]): DrawingCalibration | null {
  const mmPerUnit: number[] = [];
  for (const a of anns) {
    const n = Math.min(a.meters.length, a.drawingUnits.length);
    for (let i = 0; i < n; i++) {
      const mt = a.meters[i];
      const du = a.drawingUnits[i];
      if (mt > 0 && du > 0) mmPerUnit.push((mt * 1000) / du);
    }
  }
  const agreed = consensus(mmPerUnit, 0.05);
  if (!agreed) return null;
  return buildCalibration(
    agreed.value,
    'annotation',
    0.5,
    `annotations: ${agreed.inliers}/${agreed.total} agree, median ${agreed.median.toFixed(4)} mm/unit`
  );
}

// ─── Public entry point ──────────────────────────────────────────────────────

/**
 * Runs every calibration source in priority order and returns the first
 * resolved result. Falls back to {@link UNKNOWN_CALIBRATION} when none apply.
 */
export function calibrateDrawing(input: CalibrationInput): DrawingCalibration {
  const detectors: Array<() => DrawingCalibration | null> = [
    () => calibrateFromHeader(input.insUnits),
    () => calibrateFromDimensions(input.dimensions ?? []),
    () => calibrateFromScaleBars(input.scaleBars ?? []),
    () => calibrateFromAnnotations(input.annotations ?? []),
  ];
  for (const detect of detectors) {
    const result = detect();
    if (result && result.resolved) return result;
  }
  return UNKNOWN_CALIBRATION;
}

/**
 * Derives canvas-space physical ratios from a drawing calibration and the
 * geometry normaliser's scale factor (drawing units per canvas unit).
 */
export function deriveCanvasCalibration(
  calibration: DrawingCalibration,
  scaleFactor: number
): CanvasCalibration {
  if (
    !calibration.resolved ||
    calibration.millimetersPerDrawingUnit == null ||
    !(scaleFactor > 0)
  ) {
    return { millimetersPerCanvasUnit: null, canvasUnitsPerMeter: null };
  }
  const millimetersPerCanvasUnit = calibration.millimetersPerDrawingUnit * scaleFactor;
  return {
    millimetersPerCanvasUnit,
    canvasUnitsPerMeter: 1000 / millimetersPerCanvasUnit,
  };
}
